/**
 * Derive the smaller stored variants of a client-exported favorite image from its master
 * (`canvas-slide-favorites` D2): the master IS the `high` variant; everything else is a
 * pixel-consistent downscale/crop of it — never a re-composite against another background
 * source, and no extra network.
 *
 * Two target kinds:
 * - **ladder** (`med`/`low`): the FULL master scaled to a target height, width proportional —
 *   for 16:9 masters this yields the fixed contract sizes; a taller-than-16:9 master
 *   (bookpage) keeps its full extent at that height. Panorama skips the ladder entirely
 *   (uniform ladder, D3: low/med/high are the master itself).
 * - **thumbs** (`thumb_n`/`thumb_h`): an exact output size fed from `thumbSourceRect` (the
 *   viewport visible when saving — a full panorama squeezed to 16:9 would be unusable),
 *   contain-fitted and centered on a transparent background (parity with the legacy
 *   ImageMagick `Extent`).
 */

import { DEFAULT_EXPORT_MIME_TYPE, DEFAULT_EXPORT_QUALITY } from './export-canvas';

export interface DeriveImageVariantsInput<L extends string = string, T extends string = string> {
    /** The exported master image (the `high` variant). */
    master: Blob;
    /** Master region rendered into the thumbs. Defaults to the full master. */
    thumbSourceRect?: { x: number; y: number; width: number; height: number };
    /** Full-master downscales: key → target pixel height (width proportional). */
    ladder: Record<L, { height: number }>;
    /** Viewport thumbnails: key → exact output size. */
    thumbs: Record<T, { width: number; height: number }>;
    /** Requested encoding; the encoder may fall back (read each Blob's `type`). Default `image/webp`. */
    mimeType?: string;
    /** Quality 0..1. Default `0.9`. */
    quality?: number;
}

/** Minimal decoded-image surface (`ImageBitmap` satisfies it). */
export interface DecodedImage {
    width: number;
    height: number;
    /** Frees the backing pixel memory (`ImageBitmap.close`). Optional for test doubles. */
    close?(): void;
}

/** Minimal 2D-canvas surface used for downscaling (`HTMLCanvasElement` satisfies it). */
export interface VariantCanvas {
    getContext(id: '2d'): VariantCanvasContext | null;
    toBlob(callback: (blob: Blob | null) => void, type?: string, quality?: number): void;
}

export interface VariantCanvasContext {
    imageSmoothingEnabled: boolean;
    imageSmoothingQuality: string;
    drawImage(
        image: DecodedImage,
        sx: number, sy: number, sw: number, sh: number,
        dx: number, dy: number, dw: number, dh: number,
    ): void;
}

/** Injectable side-effecting dependencies, so {@link deriveImageVariants} is unit-testable without a DOM. */
export interface DeriveImageVariantsDeps {
    decode?: (blob: Blob) => Promise<DecodedImage>;
    createCanvas?: (width: number, height: number) => VariantCanvas;
}

function defaultDecode(blob: Blob): Promise<DecodedImage> {
    return createImageBitmap(blob) as Promise<DecodedImage>;
}

function defaultCreateCanvas(width: number, height: number): VariantCanvas {
    const el = document.createElement('canvas');
    el.width = width;
    el.height = height;
    // Structurally compatible at runtime (the default decode yields a real ImageBitmap, a valid
    // CanvasImageSource); the cast bridges the narrower DecodedImage test seam.
    return el as unknown as VariantCanvas;
}

function encode(canvas: VariantCanvas, mimeType: string, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Variant image encoding returned no data'))),
            mimeType,
            quality,
        );
    });
}

function drawScaled(
    canvas: VariantCanvas,
    image: DecodedImage,
    src: { x: number; y: number; width: number; height: number },
    dst: { x: number; y: number; width: number; height: number },
): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable for variant derivation');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, src.x, src.y, src.width, src.height, dst.x, dst.y, dst.width, dst.height);
}

/**
 * Derive ladder downscales and viewport thumbnails from one master image (see module doc).
 * Keys are preserved in the result type — a mistyped variant key is a compile error.
 */
export async function deriveImageVariants<L extends string, T extends string>(
    input: DeriveImageVariantsInput<L, T>,
    deps: DeriveImageVariantsDeps = {},
): Promise<Record<L | T, Blob>> {
    const { master, ladder, thumbs, mimeType = DEFAULT_EXPORT_MIME_TYPE, quality = DEFAULT_EXPORT_QUALITY } = input;
    const decode = deps.decode ?? defaultDecode;
    const createCanvas = deps.createCanvas ?? defaultCreateCanvas;

    const image = await decode(master);
    try {
        const fullRect = { x: 0, y: 0, width: image.width, height: image.height };
        // Keys are preserved in the result type (Record<L | T, Blob>), so a mistyped variant key
        // at a call site is a compile error instead of a silent `undefined` Blob.
        const result = {} as Record<L | T, Blob>;

        for (const key of Object.keys(ladder) as L[]) {
            const { height } = ladder[key];
            const scale = height / image.height;
            const width = Math.max(1, Math.round(image.width * scale));
            const canvas = createCanvas(width, height);
            drawScaled(canvas, image, fullRect, { x: 0, y: 0, width, height });
            result[key] = await encode(canvas, mimeType, quality);
        }

        const thumbSource = input.thumbSourceRect ?? fullRect;
        for (const key of Object.keys(thumbs) as T[]) {
            const { width, height } = thumbs[key];
            const scale = Math.min(width / thumbSource.width, height / thumbSource.height);
            const dw = Math.max(1, Math.round(thumbSource.width * scale));
            const dh = Math.max(1, Math.round(thumbSource.height * scale));
            const canvas = createCanvas(width, height);
            // Contain-fit, centered; the uncovered area stays transparent (legacy `Extent` parity).
            drawScaled(canvas, image, thumbSource, { x: (width - dw) / 2, y: (height - dh) / 2, width: dw, height: dh });
            result[key] = await encode(canvas, mimeType, quality);
        }

        return result;
    } finally {
        // Free the decoded bitmap deterministically (a 4K master is ~33 MB RGBA) instead of
        // waiting for GC; ImageBitmap implements close(), the test seam may not.
        image.close?.();
    }
}
